/**
 * ContractSemanticSearchDialog — Recherche sémantique cross-contrats
 * en langage naturel via embeddings Gemini.
 *
 * Usage :
 *   <ContractSemanticSearchDialog open={...} onOpenChange={...} />
 * Idéal en trigger depuis un bouton "Recherche IA".
 */
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Sparkles, Search, Loader2, ArrowRight, ShieldAlert, ShieldCheck,
  FileText, Building2, User, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const EXAMPLES = [
  "Quels contrats ont une exclusivité territoriale ?",
  "Contrats avec indexation ILC ou ILAT",
  "Baux commerciaux en fin de bail",
  "Prestations SaaS avec DPA RGPD",
  "Contrats à préavis long (> 3 mois)",
];

export default function ContractSemanticSearchDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const inputRef = useRef();

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      // reset au close
      setResults(null); setQ("");
    }
  }, [open]);

  const run = async (query) => {
    const text = (query ?? q).trim();
    if (text.length < 3) { toast.error("Écris au moins 3 caractères"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/contracts/search", { params: { q: text, limit: 20 } });
      setResults(data.results || []);
      setCoverage({
        embedded: data.embedded_contracts,
        total: data.total_contracts,
        pct: data.coverage_pct,
      });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const openContract = (id) => {
    onOpenChange(false);
    navigate(`/app/contracts/${id}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden"
                     data-testid="semantic-search-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="text-lg flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </div>
            Recherche sémantique
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          <div className="text-xs text-slate-500 mt-1">
            Pose une question en langage naturel — l'IA cherche dans tous les contrats.
          </div>
        </DialogHeader>

        <div className="p-5 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input ref={inputRef} value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && run()}
                    placeholder="Ex : Quels contrats ont une exclusivité territoriale ?"
                    className="pl-9 h-11 text-sm"
                    data-testid="semantic-search-input" />
            <Button onClick={() => run()} disabled={busy || q.length < 3}
                    size="sm"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 bg-blue-600 hover:bg-blue-700"
                    data-testid="semantic-search-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
            </Button>
          </div>

          {results === null && !busy && (
            <div className="space-y-2">
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                Exemples de questions
              </div>
              <div className="flex flex-wrap gap-1.5">
                {EXAMPLES.map((ex) => (
                  <button key={ex}
                    onClick={() => { setQ(ex); run(ex); }}
                    className="text-xs px-2.5 py-1.5 rounded-full border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-slate-600 hover:text-blue-700 transition"
                    data-testid="semantic-search-example">
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          {coverage && (
            <div className="text-[11px] text-slate-500 flex items-center gap-2">
              <TrendingUp className="h-3 w-3" />
              {coverage.embedded} / {coverage.total} contrats indexés ({coverage.pct}%)
              {coverage.pct < 100 && (
                <span className="text-amber-600">
                  · Les contrats non analysés par IA n'apparaissent pas
                </span>
              )}
            </div>
          )}

          {busy && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Recherche en cours…
            </div>
          )}

          {results && !busy && (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {results.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-500">
                  Aucun contrat pertinent. Essaie une formulation différente ou
                  lance l'analyse IA sur plus de contrats.
                </div>
              ) : results.map((r) => (
                <button key={r.id} onClick={() => openContract(r.id)}
                  className="w-full text-left rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition p-3 group"
                  data-testid={`semantic-result-${r.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      {r.party_type === "client"
                        ? <User className="h-3.5 w-3.5 text-emerald-600" />
                        : <Building2 className="h-3.5 w-3.5 text-amber-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 group-hover:text-blue-700 truncate">
                          {r.title || r.party_name || "Contrat"}
                        </span>
                        <Badge variant="outline" className="text-[9px] tabular-nums">
                          {Math.round(r.score * 100)}% match
                        </Badge>
                        {r.risk_score > 50 && (
                          <Badge className="bg-red-50 text-red-700 border-red-200 text-[9px]">
                            <ShieldAlert className="h-2.5 w-2.5 mr-0.5" />
                            Risque {r.risk_score}
                          </Badge>
                        )}
                        {r.risk_score > 0 && r.risk_score <= 20 && (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px]">
                            <ShieldCheck className="h-2.5 w-2.5 mr-0.5" /> Sain
                          </Badge>
                        )}
                      </div>
                      {r.party_name && (
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {r.party_name} · {r.contract_type || "—"} · {r.reference || "sans réf."}
                        </div>
                      )}
                      {r.summary && (
                        <div className="text-xs text-slate-600 mt-1.5 line-clamp-2">
                          {r.summary}
                        </div>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-blue-600 shrink-0 mt-2" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
